import { useState, type FormEvent } from "react";
import SectionHeading from "./SectionHeading";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daftkn3ep053ijub1v60";

function Newsletter() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setStatus("loading");
    setFormError("");

    const fd = new FormData(form);
    const honeypot = String(fd.get("website_alt") || "").trim();

    const isBot = honeypot !== "";
    const ok = (): boolean =>
      !isBot &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(fd.get("email") || ""));

    if (isBot || !ok()) {
      setStatus("success");
      form.reset();
      return;
    }

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const msg = [parsed?.meta?.message, parsed?.meta?.detail, parsed?.code]
        .filter(Boolean)
        .join(" · ");
      const spam = text.toLowerCase().includes("spam");
      const success = res.ok && parsed?.code === "OK" && !spam;

      if (success) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(spam ? "提交过于频繁，请稍后再试" : msg || "提交失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试");
    }
  };

  return (
    <section id="subscribe" className="relative py-16 md:py-24">
      <div className="container-x">
        <div className="relative overflow-hidden rounded-3xl border border-background-800 bg-gradient-to-br from-background-900 via-background-950 to-background-950 px-6 py-10 md:px-12 md:py-14">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl"></div>
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-accent-500/10 blur-3xl"></div>

          <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionHeading
                eyebrow="周更订阅"
                title="把好内容，放进你的收件箱"
                desc="每周 1–2 封：精选深度专题 + 当周热榜解读。内容克制，绝不轰炸你的邮箱。随时可退订。"
              />

              {status === "success" && (
                <div className="mt-6 flex items-center gap-3 rounded-xl border border-accent-500/30 bg-accent-500/10 px-5 py-4 text-accent-200">
                  <i className="ri-checkbox-circle-fill text-xl" aria-hidden="true"></i>
                  <p className="text-sm">
                    订阅成功，感谢关注吃瓜黑屋。首封专题将在本周内发送，记得留意收件箱。
                  </p>
                </div>
              )}

              {status === "error" && (
                <div className="mt-6 flex items-center gap-3 rounded-xl border border-primary-500/30 bg-primary-500/10 px-5 py-4 text-primary-200">
                  <i className="ri-error-warning-fill text-xl" aria-hidden="true"></i>
                  <p className="text-sm">{formError}</p>
                </div>
              )}
            </div>

            <form
              id="newsletter-form"
              className="lg:col-span-6"
              data-readdy-form={SUBMIT_ADDR}
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col gap-3">
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="怎么称呼你（可选）"
                  className="w-full rounded-lg border border-background-700 bg-background-950/70 px-4 py-3.5 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-accent-500"
                />
                <div>
                  <label htmlFor="sub-email" className="sr-only">
                    邮箱地址
                  </label>
                  <input
                    id="sub-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="输入你的邮箱地址 *"
                    className="w-full rounded-lg border border-background-700 bg-background-950/70 px-4 py-3.5 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-accent-500"
                  />
                </div>
                <select
                  name="interest"
                  aria-label="感兴趣的方向"
                  defaultValue="all"
                  className="w-full appearance-none rounded-lg border border-background-700 bg-background-950/70 px-4 py-3.5 text-sm text-foreground-100 outline-none transition-colors focus:border-accent-500"
                >
                  <option value="all">我对全部栏目都感兴趣</option>
                  <option value="news">娱乐前线</option>
                  <option value="behind">背后故事</option>
                  <option value="industry">行业观察</option>
                  <option value="culture">文化解读</option>
                </select>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                      正在提交…
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" aria-hidden="true"></i>
                      立即订阅
                    </>
                  )}
                </button>

                {/* 蜜罐防机器人字段，仅对自动程序可见 */}
                <div className="trap-zone" aria-hidden="true">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
                </div>
              </div>
              <p className="mt-4 text-xs leading-relaxed text-foreground-600">
                提交即表示同意接收吃瓜黑屋的周更邮件。我们不会向任何第三方共享你的信息。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Newsletter;